import { useEffect, useRef, type ReactNode } from 'react';
import type { LoginResponse } from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';

/**
 * Restores the session on first load.
 *
 * The access token lives in memory only, so after a reload there is a refresh token but no access
 * token. This exchanges one for the other before the router decides whether to show the login
 * page — without it, every refresh would look like a sign-out.
 */
export function SessionBootstrap({ children }: { children: ReactNode }) {
  const setSession = useAuthStore((state) => state.setSession);
  const setStatus = useAuthStore((state) => state.setStatus);
  const clear = useAuthStore((state) => state.clear);
  // React 18+ mounts effects twice in development; one restore attempt is enough, and a second
  // would replay a rotated refresh token and end the session.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const { refreshToken } = useAuthStore.getState();

    if (!refreshToken) {
      setStatus('unauthenticated');
      return;
    }

    void api
      .post<LoginResponse>('/auth/refresh', { refreshToken })
      .then((result) => setSession(result.user, result.tokens))
      .catch(() => clear());
  }, [clear, setSession, setStatus]);

  return <>{children}</>;
}
